import React, { useState, useEffect, useMemo } from 'react';
import Header from './components/Header';
import MetricSummary from './components/MetricSummary';
import TrancheTimeline from './components/TrancheTimeline';
import ViewToggle, { RegisterViewMode } from './components/ViewToggle';
import FilterPanel from './components/FilterPanel';
import ObligationCard from './components/ObligationCard';
import ObligationDrawer from './components/ObligationDrawer';
import ByodSyncModal from './components/ByodSyncModal';
import PrintableAuditReport from './components/PrintableAuditReport';
import KnowledgeBaseModal from './components/KnowledgeBaseModal';
import SystemsInventoryModal from './components/SystemsInventoryModal';
import MasterDataSettingsModal from './components/MasterDataSettingsModal';
import HelpModal from './components/HelpModal';

import { ControlObligation, StatutoryProvision } from './types/obligation';
import { WorkspaceProfile, ApplicabilityDecision, DecisionStatus } from './types/decision';
import { ArchitectureSystem } from './types/system';
import { MasterDataPackage } from './types/masterData';
import { StatutoryExceptionRecord } from './types/exception';
import { DEFAULT_MASTER_DATA_PACKAGE } from './utils/defaultMasterData';
import {
  getActiveWorkspace,
  saveWorkspace,
  getDecisions,
  saveDecision,
  bulkSaveDecisions,
  getCustomSystems,
  saveCustomSystem,
  deleteCustomSystem,
  getMasterData,
  saveMasterData,
  resetMasterDataToDefaults
} from './utils/indexedDbStorage';
import { formatCitation } from './utils/citationFormatter';

import {
  Shield,
  Layers,
  Scale,
  Sparkles,
  Inbox,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  Cpu,
  Settings
} from 'lucide-react';

export default function App() {
  // Master Data State (Dynamically populated from IndexedDB, defaults to 15 Sep 2026 baseline)
  const [masterData, setMasterData] = useState<MasterDataPackage>(DEFAULT_MASTER_DATA_PACKAGE);

  const controls = masterData.controls;
  const provisions = masterData.provisions;

  // Workspace & Decision State
  const [workspace, setWorkspace] = useState<WorkspaceProfile>({
    id: 'ws_default',
    name: 'Primary Enterprise Assessment',
    entity_name: 'Acme FinTech Technologies Private Limited',
    trade_brand_name: 'AcmePay & AcmeWealth',
    entity_type: 'Private Limited Company',
    cin_registration: 'U72200MH2023PTC123456',
    pan_tan: 'AAACA1234A',
    gstin: '27AAACA1234A1Z5',
    industry_sector: 'Fintech & Financial Services (BFSI)',
    incorporation_date: '2020-04-15',
    registered_address: 'Level 14, Tower B, Cyber City, Bandra Kurla Complex (BKC), Bandra East, Mumbai, Maharashtra 400051, India',
    headquarters_address: 'Level 14, Tower B, Cyber City, Bandra Kurla Complex (BKC), Bandra East, Mumbai, Maharashtra 400051, India',
    corporate_website: 'https://acmepay.in',
    corporate_email: 'compliance@acmepay.in',
    corporate_phone: '+91 22 6123 4567',
    dpo_name: 'Adv. Rajesh Sharma',
    dpo_designation: 'Data Protection Officer & Head of Privacy Compliance',
    dpo_email: 'dpo@acmepay.in',
    dpo_phone: '+91 98765 43210',
    dpo_office_address: 'Level 14, Tower B, Cyber City, BKC, Mumbai 400051, India',
    grievance_officer_name: 'Priya Iyer',
    grievance_officer_designation: 'Head of Customer Grievance Redressal (s.8(10))',
    grievance_officer_email: 'grievance@acmepay.in',
    grievance_officer_phone: '1800-123-4567',
    grievance_portal_url: 'https://acmepay.in/privacy-grievance',
    authorized_signatory_name: 'Vikramaditya Singhania',
    authorized_signatory_designation: 'Managing Director & Chief Executive Officer',
    authorized_signatory_email: 'ceo@acmepay.in',
    is_sdf: true,
    sdf_factors: [
      'High volume and velocity of personal data processed',
      'Risk of harm to Data Principals / sensitive profiling'
    ],
    sdf_gazette_notification: 'MeitY Notif. S.O. 412(E) / 2026',
    processes_children_data: true,
    parental_consent_mechanism: 'DigiLocker Parent Token & Aadhaar Virtual ID Tokenization',
    age_gating_mechanism: 'Government Token & Credit Card Verification Gate',
    processes_disabilities_data: false,
    guardian_consent_mechanism: 'Lawful Guardian Certificate via Legal Power of Attorney',
    has_cross_border_transfers: true,
    cross_border_safeguards: 'Standard Contractual Clauses & Negative List Whitelist Compliance',
    cross_border_countries: 'Singapore (AWS AP-Southeast-1), United States (Enterprise GSuite)',
    uses_consent_managers: true,
    consent_manager_providers: 'Setu Consent Manager (MeitY Registered), OneMoney AA',
    estimated_data_principals: '2,500,000+ registered active users',
    data_principal_categories: ['Retail Banking Customers', 'Merchant Partners', 'Employees & Contractors'],
    total_data_processors_count: 12,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });

  const [decisions, setDecisions] = useState<Record<string, ApplicabilityDecision>>({});
  const [systems, setSystems] = useState<ArchitectureSystem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // View Mode & Filtering State
  const [viewMode, setViewMode] = useState<RegisterViewMode>('controls');
  const [searchQuery, setSearchQuery] = useState('');
  const [actorFilter, setActorFilter] = useState('all');
  const [trancheFilter, setTrancheFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [ownerFilter, setOwnerFilter] = useState('all');

  // Modals & Drawer State
  const [selectedItem, setSelectedItem] = useState<ControlObligation | StatutoryProvision | null>(null);
  const [isDrawerControl, setIsDrawerControl] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isPrintReportOpen, setIsPrintReportOpen] = useState(false);
  const [isKnowledgeBaseOpen, setIsKnowledgeBaseOpen] = useState(false);
  const [isSystemsModalOpen, setIsSystemsModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'overview' | 'entity' | 'exceptions' | 'users' | 'actors' | 'domains' | 'tranches' | 'owners' | 'controls' | 'provisions' | 'systems' | 'reset'>('overview');
  const [knowledgeBaseTab, setKnowledgeBaseTab] = useState<'exemptions' | 'actors' | 'systems' | 'penalties'>('exemptions');

  const handleOpenSettingsWithTab = (tab?: any) => {
    if (tab) {
      setSettingsTab(tab);
    } else {
      setSettingsTab('overview');
    }
    setIsSettingsModalOpen(true);
  };

  // Global Keyboard Shortcuts (e.g. '?' or 'Shift+/' for Help & Guide, 'Esc' to close modals)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsHelpModalOpen(false);
        setIsSettingsModalOpen(false);
        setIsSyncModalOpen(false);
        setIsPrintReportOpen(false);
        setIsKnowledgeBaseOpen(false);
        setIsSystemsModalOpen(false);
        setIsDrawerOpen(false);
        return;
      }
      const activeTag = (document.activeElement?.tagName || '').toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select') {
        return;
      }
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setIsHelpModalOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load from IndexedDB on startup
  useEffect(() => {
    async function init() {
      try {
        const ws = await getActiveWorkspace();
        setWorkspace(ws);
        const decs = await getDecisions(ws.id);
        setDecisions(decs);
        const sysList = await getCustomSystems(ws.id);
        setSystems(sysList);
        const md = await getMasterData(ws.id);
        setMasterData(md);
      } finally {
        setIsLoading(false);
      }
    }
    init();
  }, []);

  const handleRefreshDecisions = async () => {
    const ws = await getActiveWorkspace();
    const decs = await getDecisions(ws.id);
    const sysList = await getCustomSystems(ws.id);
    const md = await getMasterData(ws.id);
    setWorkspace(ws);
    setDecisions(decs);
    setSystems(sysList);
    setMasterData(md);
  };

  const handleUpdateWorkspace = async (updatedWs: WorkspaceProfile) => {
    setWorkspace(updatedWs);
    await saveWorkspace(updatedWs);
  };

  const handleSaveSystem = async (sys: ArchitectureSystem) => {
    await saveCustomSystem(workspace.id, sys);
    const sysList = await getCustomSystems(workspace.id);
    setSystems(sysList);
    const md = await getMasterData(workspace.id);
    setMasterData(md);
  };

  const handleDeleteSystem = async (sysId: string) => {
    await deleteCustomSystem(workspace.id, sysId);
    const sysList = await getCustomSystems(workspace.id);
    setSystems(sysList);
    const md = await getMasterData(workspace.id);
    setMasterData(md);
  };

  const handleUpdateObligationSystems = async (targetId: string, systemIds: string[]) => {
    const existing = decisions[targetId];
    const isCtrl = targetId.startsWith('OBL-') || masterData.controls.some(c => c.id === targetId);
    const updatedDecision: ApplicabilityDecision = {
      id: targetId,
      target_id: targetId,
      target_type: isCtrl ? 'control' : 'provision',
      status: existing?.status || 'UNASSESSED',
      entity_facts: existing?.entity_facts || '',
      rationale: existing?.rationale || '',
      assigned_owner: existing?.assigned_owner || (isCtrl ? (masterData.controls.find(c => c.id === targetId)?.recommended_owner || 'General Counsel / Legal') : 'General Counsel / Legal'),
      reviewer_name: existing?.reviewer_name || '',
      review_date: existing?.review_date || new Date().toISOString().slice(0, 10),
      unresolved_blockers: existing?.unresolved_blockers || [],
      systems_in_scope: systemIds,
      updated_at: new Date().toISOString(),
      rev: (existing?.rev || 0) + 1
    };

    const newDecisionsMap = { ...decisions, [targetId]: updatedDecision };
    setDecisions(newDecisionsMap);
    await saveDecision(workspace.id, updatedDecision);

    if (isCtrl) {
      const updatedControls = masterData.controls.map(c => {
        if (c.id === targetId) {
          return { ...c, systems: systemIds.join('; ') };
        }
        return c;
      });
      const newMd = { ...masterData, controls: updatedControls };
      setMasterData(newMd);
      await saveMasterData(workspace.id, newMd);
    }
  };

  const handleBulkUpdateSystemObligations = async (systemId: string, obligationIds: string[]) => {
    const allObligationIds = [...masterData.controls.map(c => c.id), ...masterData.provisions.map(p => p.id)];
    const updatedDecisions: Record<string, ApplicabilityDecision> = {};
    const newDecisionsMap = { ...decisions };

    allObligationIds.forEach(targetId => {
      const existing = decisions[targetId];
      const isCtrl = masterData.controls.some(c => c.id === targetId);
      const currentSystems = existing?.systems_in_scope && existing.systems_in_scope.length > 0
        ? [...existing.systems_in_scope]
        : (isCtrl ? (masterData.controls.find(c => c.id === targetId)?.systems || '').split(';').map(s => s.trim()).filter(Boolean) : []);

      const shouldHave = obligationIds.includes(targetId);
      const hasNow = currentSystems.includes(systemId);

      if (shouldHave && !hasNow) {
        const newSysList = [...currentSystems, systemId];
        const updated: ApplicabilityDecision = {
          id: targetId,
          target_id: targetId,
          target_type: isCtrl ? 'control' : 'provision',
          status: existing?.status || 'UNASSESSED',
          entity_facts: existing?.entity_facts || '',
          rationale: existing?.rationale || '',
          assigned_owner: existing?.assigned_owner || (isCtrl ? (masterData.controls.find(c => c.id === targetId)?.recommended_owner || 'General Counsel / Legal') : 'General Counsel / Legal'),
          reviewer_name: existing?.reviewer_name || '',
          review_date: existing?.review_date || new Date().toISOString().slice(0, 10),
          unresolved_blockers: existing?.unresolved_blockers || [],
          systems_in_scope: newSysList,
          updated_at: new Date().toISOString(),
          rev: (existing?.rev || 0) + 1
        };
        newDecisionsMap[targetId] = updated;
        updatedDecisions[targetId] = updated;
      } else if (!shouldHave && hasNow) {
        const newSysList = currentSystems.filter(s => s !== systemId);
        const updated: ApplicabilityDecision = {
          id: targetId,
          target_id: targetId,
          target_type: isCtrl ? 'control' : 'provision',
          status: existing?.status || 'UNASSESSED',
          entity_facts: existing?.entity_facts || '',
          rationale: existing?.rationale || '',
          assigned_owner: existing?.assigned_owner || (isCtrl ? (masterData.controls.find(c => c.id === targetId)?.recommended_owner || 'General Counsel / Legal') : 'General Counsel / Legal'),
          reviewer_name: existing?.reviewer_name || '',
          review_date: existing?.review_date || new Date().toISOString().slice(0, 10),
          unresolved_blockers: existing?.unresolved_blockers || [],
          systems_in_scope: newSysList,
          updated_at: new Date().toISOString(),
          rev: (existing?.rev || 0) + 1
        };
        newDecisionsMap[targetId] = updated;
        updatedDecisions[targetId] = updated;
      }
    });

    if (Object.keys(updatedDecisions).length > 0) {
      setDecisions(newDecisionsMap);
      await bulkSaveDecisions(workspace.id, updatedDecisions);
    }
  };

  const handleSaveMasterData = async (newMd: MasterDataPackage) => {
    setMasterData(newMd);
    await saveMasterData(workspace.id, newMd);
  };

  const handleResetMasterData = async () => {
    const defaultData = await resetMasterDataToDefaults(workspace.id);
    setMasterData(defaultData);
  };

  const handleBulkApplyException = async (exception: StatutoryExceptionRecord) => {
    if (!exception.mapped_obligation_ids || exception.mapped_obligation_ids.length === 0) return;
    const updatedDecisions: Record<string, ApplicabilityDecision> = {};
    const newDecisionsMap = { ...decisions };

    exception.mapped_obligation_ids.forEach(targetId => {
      const existing = decisions[targetId];
      const isCtrl = masterData.controls.some(c => c.id === targetId);
      const updated: ApplicabilityDecision = {
        id: targetId,
        target_id: targetId,
        target_type: isCtrl ? 'control' : 'provision',
        status: 'NOT_APPLICABLE',
        entity_facts: existing?.entity_facts
          ? `${existing.entity_facts}\n• Statutory Exception: ${exception.statutory_ground} - ${exception.title}`
          : `• Statutory Exception: ${exception.statutory_ground} - ${exception.title}`,
        rationale: `Exempt under ${exception.statutory_ground} (${exception.title}): ${exception.justification}`,
        assigned_owner: existing?.assigned_owner || (isCtrl ? (masterData.controls.find(c => c.id === targetId)?.recommended_owner || 'General Counsel / Legal') : 'General Counsel / Legal'),
        reviewer_name: exception.approved_by || existing?.reviewer_name || 'Compliance Lead',
        review_date: exception.approval_date || new Date().toISOString().slice(0, 10),
        unresolved_blockers: existing?.unresolved_blockers || [],
        systems_in_scope: existing?.systems_in_scope || [],
        updated_at: new Date().toISOString(),
        rev: (existing?.rev || 0) + 1
      };
      newDecisionsMap[targetId] = updated;
      updatedDecisions[targetId] = updated;
    });

    if (Object.keys(updatedDecisions).length > 0) {
      setDecisions(newDecisionsMap);
      await bulkSaveDecisions(workspace.id, updatedDecisions);
    }
  };

  const handleAutoApplyEntityScope = async (evaluatedDecisions: Record<string, ApplicabilityDecision>) => {
    const newDecisionsMap = { ...decisions, ...evaluatedDecisions };
    setDecisions(newDecisionsMap);
    await bulkSaveDecisions(workspace.id, evaluatedDecisions);
  };

  const handleFilterBySystem = (sysId: string) => {
    setViewMode('controls');
    setSystemFilter(sysId);
  };

  // Quick Decision Handler
  const handleQuickDecision = async (targetId: string, status: DecisionStatus) => {
    const existing = decisions[targetId];
    const isCtrl = viewMode === 'controls';
    const updated: ApplicabilityDecision = {
      id: targetId,
      target_id: targetId,
      target_type: isCtrl ? 'control' : 'provision',
      status,
      entity_facts: existing?.entity_facts || '',
      rationale: existing?.rationale || `Marked as ${status} via quick decision`,
      assigned_owner: existing?.assigned_owner || 'General Counsel / Legal',
      reviewer_name: existing?.reviewer_name || 'Compliance Officer',
      review_date: new Date().toISOString().slice(0, 10),
      unresolved_blockers: existing?.unresolved_blockers || [],
      systems_in_scope: existing?.systems_in_scope || [],
      updated_at: new Date().toISOString(),
      rev: (existing?.rev || 0) + 1
    };

    const newMap = { ...decisions, [targetId]: updated };
    setDecisions(newMap);
    await saveDecision(workspace.id, updated);
  };

  // Drawer Decision Save Handler
  const handleSaveDrawerDecision = async (updated: ApplicabilityDecision) => {
    const newMap = { ...decisions, [updated.target_id]: updated };
    setDecisions(newMap);
    await saveDecision(workspace.id, updated);
  };

  const handleOpenDrawer = (item: ControlObligation | StatutoryProvision, isControl: boolean) => {
    setSelectedItem(item);
    setIsDrawerControl(isControl);
    setIsDrawerOpen(true);
  };

  const handleOpenKnowledgeBaseWithTab = (tab: 'exemptions' | 'actors' | 'systems' | 'penalties') => {
    setKnowledgeBaseTab(tab);
    setIsKnowledgeBaseOpen(true);
  };

  // Filter Logic
  const filteredControls = useMemo(() => {
    if (viewMode !== 'controls') return [];
    return controls.filter((c) => {
      const d = decisions[c.id];
      const status = d?.status || 'UNASSESSED';

      // Search Query
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const idMatch = (c.id || '').toLowerCase().includes(q);
        const titleMatch = (c.title || c.name || '').toLowerCase().includes(q);
        const scopeMatch = (c.scope || c.provision || '').toLowerCase().includes(q);
        const descMatch = (c.description || c.control || '').toLowerCase().includes(q);
        const ownerMatch = (c.recommended_owner || d?.assigned_owner || '').toLowerCase().includes(q);
        const sysMatch = (c.systems || '').toLowerCase().includes(q);
        const domainMatch = (c.domain || c.domain_title || '').toLowerCase().includes(q);
        if (!idMatch && !titleMatch && !scopeMatch && !descMatch && !ownerMatch && !sysMatch && !domainMatch) {
          return false;
        }
      }

      // Actor filter
      if (actorFilter !== 'all') {
        const actor = (c.legal_source_records?.[0]?.actor || '').toLowerCase();
        if (!actor.includes(actorFilter.toLowerCase())) {
          return false;
        }
      }

      // Tranche filter
      if (trancheFilter !== 'all') {
        const comm = c.legal_source_records?.[0]?.commencement || '2027-05-13';
        if (comm !== trancheFilter) return false;
      }

      // Domain filter
      if (domainFilter !== 'all') {
        const slug = (c.domain_slug || '').toLowerCase();
        if (slug !== domainFilter.toLowerCase()) return false;
      }

      // System filter
      if (systemFilter !== 'all') {
        const inDecision = d?.systems_in_scope && d.systems_in_scope.includes(systemFilter);
        const inDefault = (c.systems || '').includes(systemFilter);
        if (!inDecision && !inDefault) return false;
      }

      // Status filter
      if (statusFilter !== 'ALL') {
        if (status !== statusFilter) return false;
      }

      // Owner filter
      if (ownerFilter !== 'all') {
        const own = (d?.assigned_owner || c.recommended_owner || '').toLowerCase();
        if (!own.includes(ownerFilter.toLowerCase())) return false;
      }

      return true;
    });
  }, [controls, decisions, viewMode, searchQuery, actorFilter, trancheFilter, domainFilter, systemFilter, statusFilter, ownerFilter]);

  const filteredProvisions = useMemo(() => {
    if (viewMode !== 'provisions') return [];
    return provisions.filter((p) => {
      const d = decisions[p.id];
      const status = d?.status || 'UNASSESSED';

      // Search Query
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const idMatch = (p.id || '').toLowerCase().includes(q);
        const provMatch = (p.provision || '').toLowerCase().includes(q);
        const provFmtMatch = formatCitation(p.provision || '').toLowerCase().includes(q);
        const actorMatch = (p.actor || '').toLowerCase().includes(q);
        const textMatch = (p.exact_passage || '').toLowerCase().includes(q);
        const interpMatch = (p.author_interpretation || '').toLowerCase().includes(q);
        const domainMatch = (p.domain || '').toLowerCase().includes(q);
        if (!idMatch && !provMatch && !provFmtMatch && !actorMatch && !textMatch && !interpMatch && !domainMatch) {
          return false;
        }
      }

      // Actor filter
      if (actorFilter !== 'all') {
        const actor = (p.actor || '').toLowerCase();
        if (!actor.includes(actorFilter.toLowerCase())) return false;
      }

      // Tranche filter
      if (trancheFilter !== 'all') {
        if (p.commencement !== trancheFilter) return false;
      }

      // Domain filter
      if (domainFilter !== 'all') {
        const slug = (p.domain_slug || '').toLowerCase();
        if (slug !== domainFilter.toLowerCase()) return false;
      }

      // Status filter
      if (statusFilter !== 'ALL') {
        if (status !== statusFilter) return false;
      }

      // Owner filter
      if (ownerFilter !== 'all') {
        const own = (d?.assigned_owner || '').toLowerCase();
        if (!own.includes(ownerFilter.toLowerCase())) return false;
      }

      return true;
    });
  }, [provisions, decisions, viewMode, searchQuery, actorFilter, trancheFilter, domainFilter, statusFilter, ownerFilter]);

  // Metrics Calculation
  const totalActiveItems = viewMode === 'controls' ? controls.length : provisions.length;
  const currentList = viewMode === 'controls' ? controls : provisions;

  const applicableCount = currentList.filter(item => decisions[item.id]?.status === 'APPLICABLE').length;
  const notApplicableCount = currentList.filter(item => decisions[item.id]?.status === 'NOT_APPLICABLE').length;
  const unresolvedCount = currentList.filter(item => decisions[item.id]?.status === 'UNRESOLVED').length;
  const unassessedCount = totalActiveItems - (applicableCount + notApplicableCount + unresolvedCount);

  const hasActiveFilters =
    Boolean(searchQuery) ||
    actorFilter !== 'all' ||
    trancheFilter !== 'all' ||
    domainFilter !== 'all' ||
    systemFilter !== 'all' ||
    statusFilter !== 'ALL' ||
    ownerFilter !== 'all';

  const handleResetFilters = () => {
    setSearchQuery('');
    setActorFilter('all');
    setTrancheFilter('all');
    setDomainFilter('all');
    setSystemFilter('all');
    setStatusFilter('ALL');
    setOwnerFilter('all');
  };

  const displayedCount = viewMode === 'controls' ? filteredControls.length : filteredProvisions.length;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
      
      {/* Sticky Top Header */}
      <Header
        workspace={workspace}
        onOpenSyncModal={() => setIsSyncModalOpen(true)}
        onOpenPrintReport={() => setIsPrintReportOpen(true)}
        onOpenKnowledgeBase={() => handleOpenKnowledgeBaseWithTab('exemptions')}
        onOpenSettings={handleOpenSettingsWithTab}
        onOpenHelp={() => setIsHelpModalOpen(true)}
      />

      {/* Main Content Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        
        {/* Hero Banner */}
        <div className="p-6 sm:p-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs space-y-3 relative overflow-hidden">
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-mono font-extrabold bg-accent/10 dark:bg-accent/20 text-accent dark:text-accent-light border border-accent/20 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Independent Compliance Decision Workspace</span>
            </span>
            <span className="text-xs font-mono text-slate-500">
              India DPDP Act 2023 • DPDP Rules 2025 • Gazette Baseline 15 Sep 2026
            </span>
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-slate-100">
              DPDP Obligations Register & Decision Workspace
            </h1>

            <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed max-w-4xl mt-1.5">
              Complete, self-contained statutory and technical decision suite. Evaluate enterprise activities against {controls.length} operational controls and {provisions.length} statutory provisions. Classify applicability (<span className="text-emerald-600 font-semibold">APPLICABLE</span>, <span className="text-slate-600 font-semibold">NOT APPLICABLE</span>, <span className="text-amber-600 font-semibold">UNRESOLVED</span>), map obligations to your enterprise systems, customize compliance taxonomy in Settings, assign control owners, and export audit dossiers with zero external dependencies.
            </p>
          </div>
        </div>

        {/* Real-time Metric Summary & Progress Bar */}
        <MetricSummary
          total={totalActiveItems}
          applicableCount={applicableCount}
          notApplicableCount={notApplicableCount}
          unresolvedCount={unresolvedCount}
          unassessedCount={unassessedCount}
          activeStatusFilter={statusFilter}
          onSelectStatusFilter={setStatusFilter}
        />

        {/* Tranche Timeline Section */}
        <TrancheTimeline
          activeTrancheFilter={trancheFilter}
          onSelectTranche={setTrancheFilter}
          tranches={masterData.tranches}
        />

        {/* View Mode Toggle: Controls vs Statutory Provisions */}
        <ViewToggle
          viewMode={viewMode}
          onChangeViewMode={setViewMode}
          controlsCount={controls.length}
          provisionsCount={provisions.length}
        />

        {/* Multi-Dimensional Filter Bar */}
        <FilterPanel
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          actorFilter={actorFilter}
          onActorChange={setActorFilter}
          trancheFilter={trancheFilter}
          onTrancheChange={setTrancheFilter}
          domainFilter={domainFilter}
          onDomainChange={setDomainFilter}
          systemFilter={systemFilter}
          onSystemChange={setSystemFilter}
          systems={systems}
          actors={masterData.actors}
          tranches={masterData.tranches}
          domains={masterData.domains}
          owners={masterData.owners}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          ownerFilter={ownerFilter}
          onOwnerChange={setOwnerFilter}
          onResetFilters={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          totalFiltered={displayedCount}
          totalItems={totalActiveItems}
          isControlsMode={viewMode === 'controls'}
        />

        {/* Obligation Cards Grid / List */}
        <div className="space-y-3">
          {viewMode === 'controls' ? (
            filteredControls.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {filteredControls.map((control) => (
                  <ObligationCard
                    key={control.id}
                    item={control}
                    isControl={true}
                    decision={decisions[control.id]}
                    customSystems={systems}
                    onQuickDecision={handleQuickDecision}
                    onOpenDrawer={handleOpenDrawer}
                  />
                ))}
              </div>
            ) : (
              <div className="p-12 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center space-y-3">
                <Inbox className="w-10 h-10 text-slate-400 mx-auto" />
                <h4 className="text-base font-bold text-slate-800 dark:text-slate-200">
                  No operational controls match your active filters
                </h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Try clearing your search query, actor, tranche, domain, or system filter to view all 54 controls.
                </p>
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )
          ) : (
            filteredProvisions.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {filteredProvisions.map((prov) => (
                  <ObligationCard
                    key={prov.id}
                    item={prov}
                    isControl={false}
                    decision={decisions[prov.id]}
                    customSystems={systems}
                    onQuickDecision={handleQuickDecision}
                    onOpenDrawer={handleOpenDrawer}
                  />
                ))}
              </div>
            ) : (
              <div className="p-12 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center space-y-3">
                <Inbox className="w-10 h-10 text-slate-400 mx-auto" />
                <h4 className="text-base font-bold text-slate-800 dark:text-slate-200">
                  No statutory provisions match your active filters
                </h4>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Try clearing your search query or actor filter to view all 102 statutory provisions and rules.
                </p>
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-accent hover:bg-accent-dark transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )
          )}
        </div>

      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-6 text-xs text-slate-500 dark:text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-accent" />
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              DPDP Obligations Register & Decision Workspace (P01)
            </span>
          </div>
          <div className="text-[11px] font-mono">
            100% Standalone Offline PWA • Custom Enterprise Systems Architecture Mapping
          </div>
        </div>
      </footer>

      {/* Slide-over Inspection & Decision Drawer */}
      <ObligationDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        item={selectedItem}
        isControl={isDrawerControl}
        decision={selectedItem ? decisions[isDrawerControl ? (selectedItem as ControlObligation).id : (selectedItem as StatutoryProvision).id] : undefined}
        customSystems={systems}
        owners={masterData.owners}
        users={masterData.users}
        exceptions={masterData.exceptions}
        onSaveDecision={handleSaveDrawerDecision}
        onOpenKnowledgeBase={handleOpenKnowledgeBaseWithTab}
        onOpenSystemsInventory={() => setIsSystemsModalOpen(true)}
      />

      {/* Enterprise Architecture Systems & Asset Inventory Modal */}
      <SystemsInventoryModal
        isOpen={isSystemsModalOpen}
        onClose={() => setIsSystemsModalOpen(false)}
        systems={systems}
        decisions={decisions}
        controls={controls}
        onSaveSystem={handleSaveSystem}
        onDeleteSystem={handleDeleteSystem}
        onFilterBySystem={handleFilterBySystem}
      />

      {/* Master Data & Compliance Taxonomy Settings Modal */}
      <MasterDataSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        defaultTab={settingsTab}
        masterData={masterData}
        onSaveMasterData={handleSaveMasterData}
        onResetToDefaults={handleResetMasterData}
        systems={systems}
        decisions={decisions}
        workspace={workspace}
        onUpdateWorkspace={handleUpdateWorkspace}
        onSaveSystem={handleSaveSystem}
        onDeleteSystem={handleDeleteSystem}
        onUpdateObligationSystems={handleUpdateObligationSystems}
        onBulkUpdateSystemObligations={handleBulkUpdateSystemObligations}
        onBulkApplyException={handleBulkApplyException}
        onAutoApplyEntityScope={handleAutoApplyEntityScope}
      />

      {/* Portability, Backup & BYOD Sync Modal */}
      <ByodSyncModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        workspace={workspace}
        controls={controls}
        provisions={provisions}
        decisions={decisions}
        onRefreshDecisions={handleRefreshDecisions}
      />

      {/* In-App Statutory & Architecture Knowledge Base Modal */}
      <KnowledgeBaseModal
        isOpen={isKnowledgeBaseOpen}
        onClose={() => setIsKnowledgeBaseOpen(false)}
        defaultTab={knowledgeBaseTab}
        onOpenSettings={(tab) => {
          setIsKnowledgeBaseOpen(false);
          handleOpenSettingsWithTab(tab);
        }}
      />

      {/* Printable Executive Audit Report */}
      <PrintableAuditReport
        isOpen={isPrintReportOpen}
        onClose={() => setIsPrintReportOpen(false)}
        workspace={workspace}
        controls={controls}
        provisions={provisions}
        decisions={decisions}
        exceptions={masterData.exceptions}
      />

      {/* Interactive In-App Help & User Guide Modal */}
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        onOpenSettings={handleOpenSettingsWithTab}
        onOpenSyncModal={() => setIsSyncModalOpen(true)}
        onOpenPrintReport={() => setIsPrintReportOpen(true)}
        onOpenKnowledgeBase={() => handleOpenKnowledgeBaseWithTab('exemptions')}
      />

    </div>
  );
}
